import React from 'react';
import { withRouter } from 'react-router-dom';
import { cloneDeep, forEach, filter, get, noop } from 'lodash';
import searchQuery from 'query-string';

// local dependencies
import { memberAlertsListing as MemberAlertsListingView } from 'ui/views/widgets/memberAlertsListing';
import { MemberAlertsListingService } from 'services/memberAlertsListing';
import { SERVICE_RESPONSE_STATUS, ERROR_RESPONSE_TEMPLATE, SIZES } from 'constants/common';
import { ACTIONS, ALERTS_LISTING_ACTIONS, ALERT_ACTIONS, ALERT_TEXT_ACTIONS, ALERT_FILTERS } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { messageBus as MessageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { getWidgetData } from 'resolvers/getWidgetData';
import { getStore } from 'store';
import { REDUCER, WIDGET_NAMES } from 'constants/entity-names';
import { DynamicQueueTask } from 'tasks/DynamicQueue';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as backRoute from 'utils/backRoute';

// get store
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberAlertsListing } from 'ui/containers/MemberAlertsListing';
 *
 * // render
 * <MemberAlertsListing propName='propValue'/>
 */
class _MemberAlertsListing extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
        // create new service instance
        this.memberAlertsListingService = new MemberAlertsListingService();

        // static data
        this.data = {
            memberId: get( this.props.location, 'state.memberId', undefined )
        };

        // initial state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: {},

            filteredAlerts: {},
            isAccordianOpen: {}
        };

        // bind methods and listeners
        this.__bind();

        this.queue = new DynamicQueueTask( ( next ) => {
            
            // fetch widget data
            this.fetchComponentData( next );
        } );

    }

    /**
     * @desc Handles binding of methods and listeners
     */
    __bind = () => {
        log.debug( '_MemberAlertsListing.__bind()' );

        MessageBus.on( `${ WIDGET_NAMES.MEMBER_ALERTS_LISTING }_${ MESSAGE_BUS_EVENTS.WIDGET_RELOAD_REQUEST }`, this.handleWebSocketMessages, this );
        MessageBus.on( `${ WIDGET_NAMES.MEMBER_ALERTS_LISTING }_${ MESSAGE_BUS_EVENTS.WIDGET_SYNC_REQUEST }`, this.handleWebSocketMessages, this );
    }

    /**
     * @desc Handles fetching of component data
     */
    fetchComponentData = async ( runNext = noop ) => {
        log.debug( 'MemberAlertsListing.fetchComponentData()' );

        this.memberAlertsListingService.cancel();

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );
        
        try {
            const { status, data }  = await this.memberAlertsListingService.fetchComponentData( {
                url: this.props.widget.url,
                data: {
                    context: {
                        callId,
                        callType,
                        callerType,
                        memberId: this.data.memberId
                    }
                }
            } );

            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                }, () => runNext() );
            } else {

                // all accordians should be open by default
                const isAccordianOpen = {};
                forEach( data.alertTypes, alertType => { isAccordianOpen[ alertType.name ] = true; } );

                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                    isAccordianOpen
                }, () => {

                    // filter alerts on default filter
                    this.filterAlerts( this.state.componentData.state.selectedFilter );
                    runNext();
                } );
            }
        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            }, () => runNext() );
        }
    }

    /**
     * @desc Handles action clicks
     */
    handleActionClick = ( { type, value, data } ) => {
        log.debug( 'MemberAlertsListing.handleActionClick()' );

        if ( ALERTS_LISTING_ACTIONS.NAV_ACTION === type ) {
            this.handleNavActionClick( value );
        } else if ( ALERTS_LISTING_ACTIONS.FILTER_ACTION === type ) {
            this.handleFilterActionClick( value );
        } else if ( ALERTS_LISTING_ACTIONS.ACCORDIAN_ACTION === type ) {
            this.handleAccordianActionClick( value );
        } else if ( ALERTS_LISTING_ACTIONS.ALERT_ACTION === type ) {
            this.handleAlertActionClick( value, data );
        } else if ( ALERTS_LISTING_ACTIONS.ALERT_TEXT_ACTION === type ) {
            this.handleAlertTextActionClick( value, data );
        }
    }

    /**
     * @desc Handles nav action click
     */
    handleNavActionClick = ( navAction ) => {
        log.debug( 'MemberAlertsListing.handleNavActionClick()' );

        if ( ACTIONS.BACK === navAction.name ) {
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                state: { memberId: this.data.memberId }
            } );
        }
    }

    /**
     * @desc Handles filter action click
     */
    handleFilterActionClick = ( selectedFilter ) => {
        log.debug( 'MemberAlertsListing.handleFilterActionClick()' );

        const componentData = cloneDeep( this.state.componentData );
        componentData.state.selectedFilter = selectedFilter.name;

        this.setState( { componentData }, () => {
            this.filterAlerts( selectedFilter.name );
        } );
    }
    
    /**
     * @desc Filter alerts based on selected filter
     */
    filterAlerts = ( selectedFilter ) => {
        log.debug( 'MemberAlertsListing.filterAlerts()' );

        const filteredAlerts = {};
        const componentData = cloneDeep( this.state.componentData );
        
        forEach( componentData.alertTypes, alertType => {
            filteredAlerts[ alertType.name ] = filter( componentData.state[ alertType.name ], alert => {
                return ALERT_FILTERS.ALL === selectedFilter || alert.type === selectedFilter;
            } );
        } );

        this.setState( { filteredAlerts } );
    }

    /**
     * @desc Handles opening and closing of accordian
     */
    handleAccordianActionClick = ( alertType ) => {
        log.debug( 'MemberAlertsListing.handleAccordianActionClick()' );

        const isAccordianOpen = cloneDeep( this.state.isAccordianOpen );
        isAccordianOpen[ alertType.name ] = ! isAccordianOpen[ alertType.name ];
        this.setState( { isAccordianOpen } );
    }

    /**
     * @desc Handles alert action clicks
     */
    handleAlertActionClick = ( alertAction, alert ) => {
        log.debug( 'MemberAlertsListing.handleAlertActionClick()' );

        if ( ALERT_ACTIONS.SHOW_LATER === alertAction.name ) {
            this.handleShowLaterActionClick( { actionUrl: alertAction.url, alertId: alert.id } );
        }
    }

    /**
     * @desc Handles show later alert action click
     */
    handleShowLaterActionClick = async ( { actionUrl, alertId } ) => {
        log.debug( 'MemberAlertsListing.handleShowLaterActionClick()' );

        this.memberAlertsListingService.cancel();

        pageLevelPreloader.show();

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const callerType = get( store.getState()[ REDUCER.CALL_STATE ], 'callerType', null );

        const { status, data } = await this.memberAlertsListingService.submitShowLaterRequest( {
            url: actionUrl,
            data: {
                context: {
                    callId,
                    callType,
                    callerType,
                    memberId: this.data.memberId
                },
                data: { alertId }
            }
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.removeAlert( alertId );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles removal of alert based on id
     */
    removeAlert = ( alertId ) => {
        log.debug( 'MemberAlertsListing.removeAlert()' );

        const componentData = cloneDeep( this.state.componentData );

        // filter out (remove) alert, from componentData, based on its id
        forEach( componentData.alertTypes, alertType => {
            componentData.state[ alertType.name ] = filter( componentData.state[ alertType.name ], alert => alert.id !== alertId );
        } );

        // filter all alerts in componentData, again
        // to populate filteredAlerts correctly, based on selectedFilter
        this.setState( { componentData }, () => {
            this.filterAlerts( this.state.componentData.state.selectedFilter );
        } );
    }

    /**
     * Handles alert text actions
     * @param {string} actionName
     * @param {object} actionConfig
     */
    handleAlertTextActionClick = ( actionName, actionConfig ) => {
        log.debug( 'MemberAlertsListing.handleAlertTextActionClick()' );
        
        if ( ALERT_TEXT_ACTIONS.REDIRECT === actionName ) {
            this.handleRedirect( actionConfig );
        }
    }

    /**
     * Handles redirection using provided path and params
     * @param {object} config An object containing path and query params
     */
    handleRedirect = ( { path, params } ) => {
        log.debug( 'MemberAlertsListing.handleRedirect()' );
        
        // prepare back-route state payload
        const backRouteState = backRoute.encode( {
            pathname: this.props.location.pathname,
            payload: { memberId: this.data.memberId }
        } );

        this.props.history.push( {
            pathname: DASHBOARD_SUB_ROUTES[ path ],
            state: { ...backRouteState,  memberId: this.data.memberId },
            search: searchQuery.stringify( {
                ...params
            } )
        } );
    }

    /**
     * Handles WebSocket messages
     * @param {object} message - Object containing data received via WebSocket
     */
    handleWebSocketMessages = ( message ) => {
        log.debug( 'MemberAlertsListing.handleWebSocketMessages()' );

        this.queue.push( async ( next ) => {
            this.setState( await getWidgetData( {
                message,
                url: this.props.widget.url,
                initialState: this.state,
                serviceInstance: new MemberAlertsListingService()
            } ), () => {
                this.filterAlerts( this.state.componentData.state.selectedFilter );
                MessageBus.trigger( `${ MESSAGE_BUS_EVENTS.UPDATE_FLYOUT }`, { id: message.id } );
                next();
            }  );
        } );
    }

    /**
     * @desc Handles unbinding of methods and listeners
     */
     __unbind = () => {
        log.debug( '_MemberAlertsListing.__unbind()' );

        MessageBus.off( `${ WIDGET_NAMES.MEMBER_ALERTS_LISTING }_${ MESSAGE_BUS_EVENTS.WIDGET_RELOAD_REQUEST }`, this.handleWebSocketMessages, this );
        MessageBus.off( `${ WIDGET_NAMES.MEMBER_ALERTS_LISTING }_${ MESSAGE_BUS_EVENTS.WIDGET_SYNC_REQUEST }`, this.handleWebSocketMessages, this );
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.__unbind();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberAlertsListingView
                { ...this.state }
                memberId={ this.data.memberId }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_MemberAlertsListing.displayName = 'MemberAlertsListing';

// set default props
_MemberAlertsListing.defaultProps = {};

// prop types
_MemberAlertsListing.propTypes = {};


export const MemberAlertsListing = withRouter( _MemberAlertsListing );
